import {
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChange,
  SimpleChanges,
} from '@angular/core';
import { AbstractControl } from '@angular/forms';
import * as Fuse from 'fuse.js';
import { List } from 'immutable';
import {
  compact,
  defaults,
  flatten,
  isArray,
  isEmpty,
  isEqual,
  noop,
} from 'lodash';
import {
  combineLatest,
  NEVER,
  Observable,
  of,
  Subject,
  Subscription,
} from 'rxjs';
import {
  IAutoCompleteOnFetchEnd,
  IAutoCompleteOnSet,
  IAutoCompleteOptions,
  IFetchData,
} from './auto-complete.interface';
import { catchError, filter, map, switchMap, takeUntil } from 'rxjs/operators';

const horsey = require('i2g-horsey');

export const MAX_PATTERN_LENGTH = 100;
export const AUTOCOMPLETE_CLASS = 'sey-container';

const autoCompleteOptionDefaults: IAutoCompleteOptions = {
  data: [],
  searchableKeys: [],
  async: false,
  asyncCallback: (query: string) => of([]),
  itemTemplate: noop,
  categoryTemplate: noop,
  getFn: (obj: any, path: string) => obj.getIn(flatten(path.split('.'))),
  setFn: null,
  emitEventOnSet: false,
  blankSearch: true,
  highlighter: false,
  highlightCompleteWords: false,
  autoSelectFirstResult: true,
  supportMultilineInput: false,
  hideListOnFullMatch: true,
  placeholderElem: null,
};

@Directive({
  selector: '[i2gAutoComplete]',
})
export class AutoCompleteDirective implements OnInit, OnChanges, OnDestroy {
  @Input() i2gAutoComplete: IAutoCompleteOptions;
  @Input() formControl: AbstractControl;

  @Output()
  autoCompleteFetchStart: EventEmitter<void> = new EventEmitter<void>();
  @Output()
  autoCompleteFetchEnd: EventEmitter<IAutoCompleteOnFetchEnd> = new EventEmitter<IAutoCompleteOnFetchEnd>();
  @Output()
  autoCompleteSet: EventEmitter<IAutoCompleteOnSet> = new EventEmitter<IAutoCompleteOnSet>();
  @Output() autoCompleteHide: EventEmitter<void> = new EventEmitter<void>();
  @Output() autoCompleteDestroy: EventEmitter<void> = new EventEmitter<void>();

  private _autoCompleteOptions: IAutoCompleteOptions = null;
  private _autoCompleteInstance: any = null;
  private _autoCompleteCache: any = {};
  private _searchCache: any = {};
  private _fetchData: Subject<IFetchData> = new Subject<IFetchData>();
  private _fetchData$: Observable<IFetchData> = this._fetchData.asObservable();
  private _destroyed: Subject<boolean> = new Subject<boolean>();
  private _fuzzySearcher: any = null;
  private _el: any = null;
  private _onFocusToggle: Subject<boolean> = new Subject<boolean>();
  private _onToggle: Subject<boolean> = new Subject<boolean>();
  private _onFocusToggle$: Observable<boolean> = this._onFocusToggle.asObservable();
  private _onToggle$: Observable<boolean> = this._onToggle.asObservable();
  private _fetchDataSub: Subscription;

  constructor(private _elementRef: ElementRef) {}

  ngOnChanges(changes: SimpleChanges) {
    const autoCompleteChanges: SimpleChange = changes.i2gAutoComplete;

    if (!this._el) {
      this._el = this._elementRef.nativeElement;
    }

    if (autoCompleteChanges) {
      if (this.i2gAutoComplete && !isEmpty(this.i2gAutoComplete)) {
        this._autoCompleteOptions = defaults(
          {},
          this.i2gAutoComplete,
          autoCompleteOptionDefaults
        );

        this.destroyAutoCompleteInstance();

        if (this._autoCompleteOptions.async) {
          this.configureAutoComplete();
        } else {
          const data = this._autoCompleteOptions.data;

          if (data instanceof Observable) {
            data
              .pipe(
                filter(
                  (sourceData) => !isEmpty(sourceData) && isArray(sourceData)
                ),
                takeUntil(this._destroyed)
              )
              .subscribe((sourceData) => {
                if (this._autoCompleteInstance) {
                  this._autoCompleteInstance.retarget(this._el);
                } else {
                  this.destroyAutoCompleteInstance();

                  this.configureAutoComplete(sourceData);
                }
              });
          } else {
            if (this._autoCompleteInstance) {
              this._autoCompleteInstance.retarget(this._el);
            } else {
              this.destroyAutoCompleteInstance();

              this.configureAutoComplete(data);
            }
          }
        }
      } else if (!autoCompleteChanges.isFirstChange()) {
        this.destroyAutoCompleteInstance();
      }
    }
  }

  ngOnInit() {
    if (!this._el) {
      this._el = this._elementRef.nativeElement;
    }
  }

  configureAutoComplete(sourceData?: any[]) {
    const autoCompleteOptions = this._autoCompleteOptions;

    try {
      const isSynchronous = sourceData && !autoCompleteOptions.async;

      if (isSynchronous) {
        this._fuzzySearcher = new Fuse(sourceData, {
          keys: autoCompleteOptions.searchableKeys,
          getFn: autoCompleteOptions.getFn,
          location: 0,
          distance: 0,
          threshold: 0,
          shouldSort: true,
          maxPatternLength: MAX_PATTERN_LENGTH,
          minMatchCharLength: 1,
        });
      }

      if (this._fetchDataSub) {
        this._fetchDataSub.unsubscribe();
      }

      this._fetchDataSub = this._fetchData$
        .pipe(
          takeUntil(this._destroyed),
          switchMap(({ fetch, query, doneCb }: IFetchData) => {
            if (fetch) {
              if (isSynchronous) {
                return of({
                  data: sourceData,
                  query,
                  doneCb,
                });
              } else {
                this.autoCompleteFetchStart.emit();

                return autoCompleteOptions.asyncCallback(query).pipe(
                  map((list: any[]) => {
                    if (
                      autoCompleteOptions.hideListOnFullMatch &&
                      list.length === 1 &&
                      query ===
                        (autoCompleteOptions.setFn
                          ? autoCompleteOptions.setFn(list[0])
                          : list[0])
                    ) {
                      return {
                        data: [],
                        query,
                        doneCb,
                      };
                    }

                    const placeholderElem = autoCompleteOptions.placeholderElem;
                    const placeholderEl = compact([
                      placeholderElem
                        ? typeof placeholderElem === 'function'
                          ? placeholderElem(query, list)
                          : placeholderElem
                        : null,
                    ]);

                    return {
                      data: list.concat(placeholderEl),
                      query,
                      doneCb,
                    };
                  }),
                  catchError((error) =>
                    of({
                      data: [],
                      query,
                      doneCb,
                    })
                  )
                );
              }
            } else {
              this.autoCompleteFetchEnd.emit({
                list: null,
                query: null,
              });

              return NEVER;
            }
          }),
          takeUntil(this._destroyed)
        )
        .subscribe(
          ({ data, query, doneCb }) => {
            doneCb(null, [{ list: data || [] }]);

            this.autoCompleteFetchEnd.emit({
              list: List(data || []),
              query,
            });
          },
          () =>
            this.autoCompleteFetchEnd.emit({
              list: null,
              query: null,
            })
        );

      this._autoCompleteInstance = horsey(this._el, {
        appendTo: this._el.parentElement,
        highlighter: autoCompleteOptions.highlighter,
        highlightCompleteWords: autoCompleteOptions.highlightCompleteWords,
        autoSelectFirstResult: autoCompleteOptions.autoSelectFirstResult,
        supportMultilineInput: autoCompleteOptions.supportMultilineInput,
        blankSearch: autoCompleteOptions.blankSearch,
        cache: this._autoCompleteCache,
        limit: autoCompleteOptions.limit || null,
        debounce: isSynchronous ? 100 : 500,
        source: (data, done) => {
          // because this is asynchronous due to the debounce, check if
          // the autocomplete instance was already destroyed or not
          // (it would have been destroyed if `this._autoCompleteInstance` is falsy)
          if (this._autoCompleteInstance) {
            this._fetchData.next({
              fetch: true,
              query: data.input,
              doneCb: done,
            });
          }
        },
        filter: (query: string, suggestion: any) => {
          query = this.compactSpaces(query);

          if (isSynchronous) {
            if (!this._searchCache[query]) {
              if (query && query.length > MAX_PATTERN_LENGTH) {
                this._searchCache[query] = [];
              } else {
                this._searchCache[query] =
                  query && this._fuzzySearcher
                    ? this._fuzzySearcher.search(query)
                    : sourceData;
              }
            }

            return this._searchCache[query].some(
              (item) =>
                (item.equals && item.equals(suggestion)) ||
                isEqual(item, suggestion)
            );
          } else {
            return true;
          }
        },
        renderItem: autoCompleteOptions.itemTemplate,
        renderCategory: (categoryDom: any) =>
          autoCompleteOptions.categoryTemplate(
            categoryDom,
            this.formControl.value
          ),
        getText: (text) => text,
        getValue: (value) => value,
        set: (value: any) => {
          const currentFieldValue = this.formControl.value || '';

          this.formControl.setValue(
            autoCompleteOptions.setFn
              ? autoCompleteOptions.setFn(value)
              : value,
            {
              onlySelf: !autoCompleteOptions.emitEventOnSet,
              emitEvent: autoCompleteOptions.emitEventOnSet,
            }
          );

          this.autoCompleteSet.emit({
            query: currentFieldValue,
            value,
          });
        },
      });

      combineLatest(this._onToggle$, this._onFocusToggle$)
        .pipe(
          filter(([toggle, focused]) => !toggle && !focused),
          takeUntil(this._destroyed)
        )
        .subscribe(() => this.autoCompleteHide.emit());

      this._el.addEventListener('horsey-hide', this.triggerToggleFalse);
      this._el.addEventListener('horsey-show', this.triggerToggleTrue);
      this._el.addEventListener('focus', this.showOnFocusIfEmpty);
      this._el.addEventListener('focusout', this.emitBlurEvent);
    } catch (e) {}
  }

  compactSpaces = (str: string) => {
    return str
      ? str.replace(/(\r\n|\n|\r)/gm, '').replace(/\s{2,}/g, ' ')
      : str;
  };

  emitBlurEvent = () => {
    this._onFocusToggle.next(false);
  };

  showOnFocusIfEmpty = () => {
    if (this._el && !this._el.value && this._autoCompleteInstance) {
      this._autoCompleteInstance.show();
    }

    this._onFocusToggle.next(true);
  };

  triggerToggleFalse = () => {
    this._onToggle.next(false);
  };

  triggerToggleTrue = () => {
    this._onToggle.next(true);
  };

  destroyAutoCompleteInstance() {
    this._fetchData.next({ fetch: false });

    if (this._el) {
      this._el.removeEventListener('horsey-hide', this.triggerToggleTrue);
      this._el.removeEventListener('horsey-show', this.triggerToggleFalse);
      this._el.removeEventListener('focus', this.showOnFocusIfEmpty);
      this._el.removeEventListener('focusout', this.emitBlurEvent);
    }

    if (this._autoCompleteInstance) {
      this._autoCompleteInstance.destroy();
      this._autoCompleteInstance = null;
      this._searchCache = {};
      this._autoCompleteCache = {};
    }

    this._fuzzySearcher = null;

    if (this._fetchDataSub) {
      this._fetchDataSub.unsubscribe();
    }

    this._destroyed.next(true);
    this.autoCompleteDestroy.emit();
  }

  ngOnDestroy() {
    this.destroyAutoCompleteInstance();

    this._destroyed.unsubscribe();
  }
}
